import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../../../utils/i18nStub';
import './MosqueFilters.css';

// Import services following the same pattern as donations
import { fetchRegions, fetchCitiesByRegion, fetchCentersByCity } from "../../../services/geoService";
// import { getMosqueClassifications } from "../../../services/mosqueService"; // TODO: Uncomment when backend endpoint is ready
import { MosqueFilterState, Option } from "../../../types/mosque";

// ✅ ADDED: فعّلنا الاستدعاء الحقيقي مع إبقاء التعليق الأصلي فوق
import { getMosqueClassifications } from "../../../services/mosqueService";

// ---- Filter model (same shape used by the mosque search page) ----
export type { MosqueFilterState };

type Props = {
  initial: MosqueFilterState;
  onApply: (f: MosqueFilterState) => void;
  /** When true, render only the filter row (no container, no buttons). For use inside a shared container. */
  embedded?: boolean;
};

// Helper to convert API response to option format
const toOption = (row: any): Option => ({
  id: row?.id ?? row?.Id ?? 0,
  name: row?.name ?? row?.Name ?? "",
});

// -------------------------------------------------------------
// MosqueFilters (all dropdowns are NON-mandatory now)
// -------------------------------------------------------------
const MosqueFilters: React.FC<Props> = ({ initial, onApply, embedded = false }) => {
  const { t } = useTranslation("mosque");

  // local form state (editable) - using donation pattern
  const [form, setForm] = useState<MosqueFilterState>(() => ({ ...initial }));

  // dropdown data - using donation pattern
  const [regions, setRegions] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [centers, setCenters] = useState<Option[]>([]);
  const [classifications, setClassifications] = useState<Option[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [loadingClassifications, setLoadingClassifications] = useState(false);

  // Load regions on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingRegions(true);
      try {
        const rows = await fetchRegions(); // public endpoint
        if (!alive) return;
        setRegions((rows ?? []).map(toOption));
      } finally {
        if (alive) setLoadingRegions(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load classifications on mount - start with mock and then try real API
  useEffect(() => {
    // Mock classifications data (fallback)
    const mockClassifications = [
      { id: 1, name: 'مسجد جامع' },
      { id: 2, name: 'مسجد فروض' },
      { id: 3, name: 'مصلى عيد' }      
    ];
    setClassifications(mockClassifications.map(toOption));

    // ✅ Try real API, fallback silently to mock if empty/error
    let alive = true;
    (async () => {
      setLoadingClassifications(true);
      try {
        const rows = await getMosqueClassifications();
        if (!alive) return;
        if (rows && Array.isArray(rows) && rows.length > 0) {
          setClassifications(rows.map(toOption));
        }
      } catch {
        // ignore → keep mock
      } finally {
        if (alive) setLoadingClassifications(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // When region changes, fetch its cities (and clear city/center if region changed)
  useEffect(() => {
    let alive = true;

    if (!form.regionId) {
      setCities([]);
      if (form.cityId) {
        setForm((f) => ({ ...f, cityId: null, centerId: null }));
      }
      return;
    }

    (async () => {
      setLoadingCities(true);
      try {
        const rows = await fetchCitiesByRegion(form.regionId);
        if (!alive) return;
        const opts = (rows ?? []).map(toOption);
        setCities(opts);

        // If selected city not in new list, clear it
        if (form.cityId && !opts.some((c) => c.id === form.cityId)) {
          setForm((f) => ({ ...f, cityId: null, centerId: null }));
        }
      } finally {
        if (alive) setLoadingCities(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.regionId]);

  // When city changes, fetch its centers (and clear center if city changed)
  useEffect(() => {
    let alive = true;

    if (!form.cityId) {
      setCenters([]);
      if (form.centerId) {
        setForm((f) => ({ ...f, centerId: null }));
      }
      return;
    }

    (async () => {
      setLoadingCenters(true);
      try {
        const rows = await fetchCentersByCity(form.cityId);
        if (!alive) return;
        const opts = (rows ?? []).map(toOption);
        setCenters(opts);

        // If selected center not in new list, clear it
        if (form.centerId && !opts.some((c) => c.id === form.centerId)) {
          setForm((f) => ({ ...f, centerId: null }));
        }
      } finally {
        if (alive) setLoadingCenters(false);
      }
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cityId]);

  // Handlers - using donation pattern
  const onChange = (patch: Partial<MosqueFilterState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  // apply no longer validates; it simply applies whatever is selected (all optional).
  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    onApply(form);
  };

  const reset = () => {
    setForm({
      regionId: null,
      cityId: null,
      centerId: null,
      classificationId: null,
      mosqueTypeId: null,
      hasWomenSection: null,
      // ✅ NEW
      mosqueName: null,
      mosqueNumber: null,
    });
    setCities([]);
    setCenters([]);
    onApply({
      regionId: null,
      cityId: null,
      centerId: null,
      classificationId: null,
      mosqueTypeId: null,
      hasWomenSection: null,
      // ✅ NEW
      mosqueName: null,
      mosqueNumber: null,
    });
  };

  // When embedded, sync form to parent on every change so parent has current values when user clicks Apply
  useEffect(() => {
    if (embedded) onApply(form);
  }, [embedded, form]);

  // Placeholders and labels from translations
  const ph = useMemo(
    () => ({
      region: t("filters.regionIdPH", "اختر المنطقة"),
      city: t("filters.cityIdPH", "اختر المدينة"),
      center: t("filters.centerIdPH", "مركز (اختياري)"),
      classification: t("filters.classificationPH", "الكل"),
      womenSection: t("filters.womenSectionPH", "الكل"),
      // ✅ NEW
      mosqueName: t("filters.mosqueNamePH", "اسم المسجد"),
      mosqueNumber: t("filters.mosqueNumberPH", "رقم المسجد"),

      
      search: t("filters.search", "بحث"),
      reset: t("filters.reset", "مسح"),
      loading: t("filters.loading", "جاري التحميل..."),
      selectRegionFirst: t("filters.selectRegionFirst", "اختر المنطقة أولاً"),
      selectCityFirst: t("filters.selectCityFirst", "اختر المدينة أولاً"),
    }),
    [t]
  );

  const filtersRow = (
    <div className="flex items-end gap-2 flex-wrap">
          {/* Region (optional) */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1" style={{
              fontFamily: 'var(--font-family-arabic)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              {t("filters.region", "المنطقة")}
            </label>
            <div className="filter-input-wrapper">
              <select
                value={form.regionId ?? ""}
                data-has-value={Boolean(form.regionId)}
                onChange={(e) => {
                  onChange({ regionId: e.target.value ? Number(e.target.value) : null });
                }}
                className="modern-filter-select font-arabic"
                disabled={loadingRegions}
                style={{
                  fontFamily: 'var(--font-family-arabic)',
                  fontWeight: 'var(--font-weight-normal)',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'unset',
                  backgroundSize: 'unset',
                  backgroundOrigin: 'unset',
                  backgroundClip: 'unset',
                  paddingInlineEnd: '36px',
                  paddingInlineStart: '36px'
                }}
              >
                <option value="">
                  {loadingRegions ? ph.loading : ph.region}
                </option>
                {regions.map(region => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
              <div className="filter-arrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>
          </div>

          {/* City (optional; still depends on region selection for data) */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1" style={{
              fontFamily: 'var(--font-family-arabic)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              {t("filters.city", "المدينة")}
            </label>
            <div className="filter-input-wrapper">
              <select
                value={form.cityId ?? ""}
                data-has-value={Boolean(form.cityId)}
                onChange={(e) => {
                  onChange({ cityId: e.target.value ? Number(e.target.value) : null });
                }}
                className="modern-filter-select font-arabic"
                disabled={!form.regionId || loadingCities}
                style={{
                  fontFamily: 'var(--font-family-arabic)',
                  fontWeight: 'var(--font-weight-normal)',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'unset',
                  backgroundSize: 'unset',
                  backgroundOrigin: 'unset',
                  backgroundClip: 'unset',
                  paddingInlineEnd: '36px',
                  paddingInlineStart: '36px'
                }}
              >
                <option value="">
                  {!form.regionId
                    ? ph.selectRegionFirst
                    : loadingCities
                      ? ph.loading
                      : ph.city
                  }
                </option>
                {cities.map(city => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
              <div className="filter-arrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>
          </div>

          {/* Center (optional; depends on city selection for data) */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1" style={{
              fontFamily: 'var(--font-family-arabic)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              {t("filters.center", "المركز")}
              <span className="optional-badge text-[0.65rem] px-1.5 py-0.5">{t("filters.optional", "اختياري")}</span>
            </label>
            <div className="filter-input-wrapper">
              <select
                value={form.centerId ?? ""}
                data-has-value={Boolean(form.centerId)}
                onChange={(e) => {
                  onChange({ centerId: e.target.value ? Number(e.target.value) : null });
                }}
                className="modern-filter-select font-arabic"
                disabled={!form.cityId || loadingCenters}
                style={{
                  fontFamily: 'var(--font-family-arabic)',
                  fontWeight: 'var(--font-weight-normal)',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'unset',
                  backgroundSize: 'unset',
                  backgroundOrigin: 'unset',
                  backgroundClip: 'unset',
                  paddingInlineEnd: '36px',
                  paddingInlineStart: '36px'
                }}
              >
                <option value="">
                  {!form.cityId
                    ? ph.selectCityFirst
                    : loadingCenters
                      ? ph.loading
                      : ph.center
                  }
                </option>
                {centers.map(center => (
                  <option key={center.id} value={center.id}>
                    {center.name}
                  </option>
                ))}
              </select>
              <div className="filter-arrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>
          </div>

          {/* Mosque Classification (optional) */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1" style={{
              fontFamily: 'var(--font-family-arabic)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              {t("filters.classification", "تصنيف المسجد")}
            </label>
            <div className="filter-input-wrapper">
              <select
                value={form.classificationId ?? ""}
                data-has-value={Boolean(form.classificationId)}
                onChange={(e) => {
                  onChange({ classificationId: e.target.value ? Number(e.target.value) : null });
                }}
                className="modern-filter-select font-arabic"
                disabled={loadingClassifications}
                style={{
                  fontFamily: 'var(--font-family-arabic)',
                  fontWeight: 'var(--font-weight-normal)',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'unset',
                  backgroundSize: 'unset',
                  backgroundOrigin: 'unset',
                  backgroundClip: 'unset',
                  paddingInlineEnd: '36px',
                  paddingInlineStart: '36px'
                }}
              >
                <option value="">
                  {loadingClassifications ? t("filters.loading", "جاري التحميل...") : ph.classification}
                </option>
                {classifications.map(classification => (
                  <option key={classification.id} value={classification.id}>
                    {classification.name}
                  </option>
                ))}
              </select>
              <div className="filter-arrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>
          </div>

          {/* Women Section (optional) */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label className="filter-label font-arabic text-xs mb-1" style={{
              fontFamily: 'var(--font-family-arabic)',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              {t("filters.womenSection", "قسم النساء")}
            </label>
            <div className="filter-input-wrapper">
              <select
                value={form.hasWomenSection === null ? "" : form.hasWomenSection.toString()}
                data-has-value={form.hasWomenSection !== null}
                onChange={(e) => {
                  onChange({
                    hasWomenSection: e.target.value === "" ? null : e.target.value === "true"
                  });
                }}
                className="modern-filter-select font-arabic"
                style={{
                  fontFamily: 'var(--font-family-arabic)',
                  fontWeight: 'var(--font-weight-normal)',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'unset',
                  backgroundSize: 'unset',
                  backgroundOrigin: 'unset',
                  backgroundClip: 'unset',
                  paddingInlineEnd: '36px',
                  paddingInlineStart: '36px'
                }}
              >
                <option value="">{ph.womenSection}</option>
                <option value="true">{t("filters.hasWomenSection", "يوجد")}</option>
                <option value="false">{t("filters.noWomenSection", "لا يوجد")}</option>
              </select>
              <div className="filter-arrow">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>
          </div>


          
          {/* ✅ NEW: Mosque Name (optional text filter) */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label
              className="filter-label font-arabic text-xs mb-1"
              style={{
                fontFamily: 'var(--font-family-arabic)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              {t("filters.mosqueName", "اسم المسجد")}
            </label>

            <div className="filter-input-wrapper">
              <input
                type="text"
                value={form.mosqueName ?? ""}
                onChange={(e) => {
                  onChange({ mosqueName: e.target.value || null });
                }}
                className="modern-filter-select font-arabic"
                placeholder={ph.mosqueName}
                style={{
                  fontFamily: 'var(--font-family-arabic)',
                  fontWeight: 'var(--font-weight-normal)',
                  paddingInlineEnd: '12px',
                  paddingInlineStart: '12px'
                }}
              />
            </div>
          </div>


          {/* ✅ NEW: Mosque Number (optional text filter) */}
          <div className="filter-group flex-1 min-w-[140px]">
            <label
              className="filter-label font-arabic text-xs mb-1"
              style={{
                fontFamily: 'var(--font-family-arabic)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              {t("filters.mosqueNumber", "رقم المسجد")}
            </label>

            <div className="filter-input-wrapper">
              <input
                type="text"
                value={form.mosqueNumber ?? ""}
                onChange={(e) => {
                  onChange({ mosqueNumber: e.target.value || null });
                }}
                className="modern-filter-select font-arabic"
                placeholder={ph.mosqueNumber}
                style={{
                  fontFamily: 'var(--font-family-arabic)',
                  fontWeight: 'var(--font-weight-normal)',
                  paddingInlineEnd: '12px',
                  paddingInlineStart: '12px'
                }}
              />
            </div>
          </div>




          {!embedded && (
            <>
              <button
                type="button"
                onClick={reset}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3,6 5,6 21,6" />
                  <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                </svg>
                {ph.reset}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-sa-600 text-white rounded-lg hover:bg-sa-700 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                {ph.search}
              </button>
            </>
          )}
        </div>
  );

  if (embedded) return filtersRow;

  return (
    <div className="modern-filters-container">
      <form onSubmit={apply} className="modern-filters-form">
        {filtersRow}
      </form>
    </div>
  );
};

export default MosqueFilters;
